import React from 'react';

import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import MailIcon from '@mui/icons-material/Mail';

import { EventAccess, EventFormData } from '../data_types/types';
import { DS } from '../shared/Dashboard';
import { InvitePlayerInput, InvitedPlayersList } from '../shared/InvitePlayers';

interface AccessOptionProps {
    selected: boolean;
    icon: React.ReactNode;
    color: string;
    title: string;
    description: string;
    onSelect: () => void;
}

const AccessOption: React.FC<AccessOptionProps> = ({ selected, icon, color, title, description, onSelect }) => (
    <Box
        component="button"
        type="button"
        role="radio"
        aria-checked={selected}
        onClick={onSelect}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: '100%',
            px: 2,
            py: 1.25,
            textAlign: 'left',
            fontFamily: 'inherit',
            borderRadius: '8px',
            border: `1px solid ${selected ? alpha(color, 0.5) : DS.borderStrong}`,
            background: selected ? alpha(color, 0.08) : 'transparent',
            cursor: 'pointer',
            transition: 'border-color 0.2s ease, background 0.2s ease',
            '&:hover': { borderColor: selected ? color : DS.textFaint },
        }}
    >
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            width: 36,
            height: 36,
            borderRadius: '8px',
            background: selected ? alpha(color, 0.16) : DS.inset,
            color: selected ? color : DS.textMuted,
        }}>
            {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: DS.text }}>{title}</Typography>
            <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>{description}</Typography>
        </Box>
    </Box>
);

interface EventAccessDetailsFormProps {
    formData: EventFormData;
    onAccessChange: (eventAccess: EventAccess) => void;
    onInvitedPlayersChange: (playerIds: string[]) => void;
}

const EventAccessDetailsForm: React.FC<EventAccessDetailsFormProps> = ({ formData, onAccessChange, onInvitedPlayersChange }) => {
    const isInvite = formData.eventAccess === EventAccess.Invite;
    const invitedPlayers = formData.invitedPlayers || [];

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box role="radiogroup" aria-label="Event access" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                <AccessOption
                    selected={!isInvite}
                    icon={<VpnKeyIcon sx={{ fontSize: 20 }} />}
                    color={DS.info}
                    title="Standard"
                    description="Goes live as Private (players join with the event code) or Public"
                    onSelect={() => onAccessChange(EventAccess.Private)}
                />
                <AccessOption
                    selected={isInvite}
                    icon={<MailIcon sx={{ fontSize: 20 }} />}
                    color={DS.violet}
                    title="Invite only"
                    description="Only the players you invite see the event in the game"
                    onSelect={() => onAccessChange(EventAccess.Invite)}
                />
            </Box>

            {isInvite && (
                <Box sx={{ p: 2, borderRadius: '12px', background: DS.inset, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: DS.text }}>Players to invite</Typography>
                        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>{invitedPlayers.length} added</Typography>
                    </Box>
                    <InvitePlayerInput
                        existing={invitedPlayers}
                        onAdd={(ids) => onInvitedPlayersChange([...invitedPlayers, ...ids])}
                    />
                    <InvitedPlayersList
                        playerIds={invitedPlayers}
                        onRemove={(id) => onInvitedPlayersChange(invitedPlayers.filter((playerId) => playerId !== id))}
                        emptyText="No players yet. You can also invite players after the event is live."
                    />
                    <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>
                        Invites are sent when an admin makes the event invite-only. Until then the list is only saved with the pending event.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default EventAccessDetailsForm;
